import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { CheckCircle2, Loader2, ShieldCheck, ShieldAlert, Undo2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  actOnLegalReview,
  approveDealAsManagement,
  getLegalReviewForDeal,
  submitDealForLegalReview
} from '../../services/api/implementation/impl';
import LegalFieldEvidence from './LegalFieldEvidence';
import LegalFlowStrip from './LegalFlowStrip';
import {
  canGiveFinalLegalApproval,
  canRecordLegalVerdict,
  canSubmitForLegalReview,
  canViewLegalReview,
  isLegallyCleared,
  LEGAL_STATUS,
  legalStatusClasses,
  legalStatusLabel
} from '../../../shared/utils/legalAccess';

const sessionFromCookies = () => ({
  vertical: Cookies.get('userVertical'),
  level: Cookies.get('userDesignationLevel'),
  role: Cookies.get('userRole')
});

/**
 * The legal half of the Post Sales tab.
 *
 * Sales sees a status and a Submit button. Legal sees the verdict actions.
 * Management sees both plus the final approval. Everyone sees the same history,
 * because the round trips are the part people argue about later.
 */
const LegalReviewPanel = ({ dealId, kycComplete, onChange }) => {
  const session = sessionFromCookies();
  const [note, setNote] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['legalReview', dealId],
    queryFn: () => getLegalReviewForDeal(dealId),
    enabled: Boolean(dealId) && canViewLegalReview(session)
  });

  const done = (message) => {
    toast.success(message);
    setNote('');
    refetch();
    onChange?.();
  };

  const fail = (err) =>
    toast.error(err?.response?.data?.message || err?.message || 'Action failed');

  const submitMutation = useMutation({
    mutationFn: () => submitDealForLegalReview(dealId),
    onSuccess: () => done('Sent to Legal'),
    onError: fail
  });

  const actMutation = useMutation({
    mutationFn: ({ action, body }) => {
      const reviewId = data?.current_round?.id;
      // Without this the request goes to /legalReview/undefined/<action> and
      // comes back as a parse error that says nothing about what went wrong.
      if (!reviewId) {
        return Promise.reject(new Error('This deal has no open legal review — refresh and try again'));
      }
      return actOnLegalReview(reviewId, action, body);
    },
    onSuccess: (_result, variables) => done(variables.successMessage),
    onError: fail
  });

  if (!canViewLegalReview(session)) return null;

  const status = data?.legal_status || LEGAL_STATUS.NOT_APPLICABLE;
  const round = data?.current_round;
  const canVerdict = canRecordLegalVerdict(session) && Boolean(round);
  const cleared = isLegallyCleared(status);
  // Optional countersignature, offered only once Legal has cleared the deal and
  // only while nobody has signed it yet.
  const canCountersign =
    canGiveFinalLegalApproval(session) && status === LEGAL_STATUS.LEGAL_VERIFIED;
  const canSubmit =
    canSubmitForLegalReview(session) &&
    (status === LEGAL_STATUS.NOT_APPLICABLE ||
      status === LEGAL_STATUS.AWAITING_DOCUMENTS ||
      status === LEGAL_STATUS.QUERY_RAISED ||
      status === LEGAL_STATUS.REJECTED);
  const approveMutation = useMutation({
    mutationFn: (body) => approveDealAsManagement(dealId, body?.note),
    onSuccess: () => done('Approved by Management'),
    onError: fail
  });

  const busy =
    submitMutation.isPending || actMutation.isPending || approveMutation.isPending;

  const act = (action, successMessage, requiresNote) => {
    if (requiresNote && note.trim().length < 3) {
      toast.error('Add a short reason first — it goes on the record');
      return;
    }
    actMutation.mutate({
      action,
      body: note.trim() ? { note: note.trim() } : {},
      successMessage
    });
  };

  return (
    <div className="mt-4 rounded-xl border border-gray-200 bg-white">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Legal verification</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            Legal&apos;s verification releases the deal. Management approval is optional.
          </p>
        </div>
        <span
          className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${legalStatusClasses(
            status
          )}`}>
          {legalStatusLabel(status)}
        </span>
      </div>

      <div className="border-b border-gray-100 px-5 py-3">
        <LegalFlowStrip status={status} compact />
      </div>

      <div className="px-5 py-4">
        {isLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading legal status…
          </div>
        ) : (
          <>
            {cleared ? (
              <div className="mb-4 flex items-start gap-2 rounded-lg bg-emerald-50 px-3 py-2.5 text-sm text-emerald-900">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>
                  <span className="font-semibold">Legally verified.</span> Priorities,
                  invoices and agreements can proceed.
                  {data?.management_approved ? (
                    <> Countersigned by Management.</>
                  ) : (
                    <>
                      {' '}
                      A Management approval is optional and is not holding anything up.
                    </>
                  )}
                </span>
              </div>
            ) : null}

            {round?.decision_note ? (
              <div className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <span className="font-semibold">Last note: </span>
                {round.decision_note}
              </div>
            ) : null}

            {status === LEGAL_STATUS.QUERY_RAISED ? (
              <p className="mb-4 text-sm text-amber-800">
                Legal has sent this back. Fix what the note asks for, then submit again.
              </p>
            ) : null}

            {canSubmit && !kycComplete ? (
              <p className="mb-4 text-sm text-gray-500">
                Upload the PAN and a second ID above before submitting to Legal.
              </p>
            ) : null}

            {(canVerdict || canCountersign) && (
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Note — required to query, reject or reopen"
                className="mb-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
              />
            )}

            <div className="flex flex-wrap gap-2">
              {canSubmit ? (
                <button
                  type="button"
                  disabled={busy || !kycComplete}
                  onClick={() => submitMutation.mutate()}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#2A565E] px-3 py-2 text-xs font-semibold text-white hover:bg-[#1B3B41] disabled:opacity-50">
                  {submitMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : null}
                  Submit for legal review
                </button>
              ) : null}

              {canVerdict && status !== LEGAL_STATUS.LEGAL_VERIFIED ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => act('verify', 'Marked legally verified', false)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-600/40 bg-white px-3 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-50 disabled:opacity-50">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Mark legally verified
                </button>
              ) : null}

              {canCountersign ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => approveMutation.mutate({ note: note.trim() || undefined })}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-800 disabled:opacity-50">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Approve as Management
                </button>
              ) : null}

              {canVerdict ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => act('query', 'Sent back to Sales', true)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-amber-600/40 bg-white px-3 py-2 text-xs font-semibold text-amber-800 hover:bg-amber-50 disabled:opacity-50">
                  <Undo2 className="h-3.5 w-3.5" />
                  Send back to Sales
                </button>
              ) : null}

              {canVerdict ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => act('reject', 'Deal rejected', true)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-rose-600/40 bg-white px-3 py-2 text-xs font-semibold text-rose-800 hover:bg-rose-50 disabled:opacity-50">
                  <ShieldAlert className="h-3.5 w-3.5" />
                  Reject
                </button>
              ) : null}
            </div>

            <div className="-mx-5 mt-5 border-t border-gray-100">
              <LegalFieldEvidence
                dealId={dealId}
                fields={data?.fields ?? []}
                canDecide={canVerdict}
                onChange={refetch}
              />
            </div>

            {data?.blocking_fields?.length ? (
              <div className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Still to decide: {data.blocking_fields.join(', ')}. A field that matched
                cleanly needs no click; anything else has to be settled before this deal
                can be marked verified.
              </div>
            ) : null}

            {data?.rounds?.length ? (
              <div className="mt-5 border-t border-gray-100 pt-4">
                <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  History
                </h4>
                <ul className="space-y-2">
                  {data.rounds.map((r) => (
                    <li key={r.id} className="flex flex-wrap gap-x-3 text-xs text-gray-600">
                      <span className="font-semibold text-gray-800">Round {r.round}</span>
                      <span>{legalStatusLabel(r.status)}</span>
                      {r.legal_verifier?.name ? (
                        <span>verified by {r.legal_verifier.name}</span>
                      ) : null}
                      {r.approver?.name ? <span>approved by {r.approver.name}</span> : null}
                      {r.decision_note ? (
                        <span className="text-gray-500">— {r.decision_note}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default LegalReviewPanel;
